import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { isNil } from 'ramda';
import { ComponentProps } from '../../../..';
import useFireTrackingEvent from '../../../../../../hooks/useFireTrackingEvent';
import { iframeSizes } from '../../../../../../shared/constants';
import { Appearance } from '../../../../../../shared/types';
import Icon from '../../../../../Icon';
import { Heading2 } from '../../../../../Typography';

import useLocale from '../../../../../../hooks/useLocale';
import useWidgetContext from '../../../../../../hooks/useWidgetContext';
import usePathPrefix from '../../../../../../hooks/usePathPrefix';
import cleanPath from '../../../../../../utils/cleanPath';

import useStateContext from '../../../../../../hooks/useStateContext';
import ListItem from './components/ListItem';
import arrayToCss from '../../../../../../utils/arrayToCss';
import CloseButton, { CLOSE_BUTTON_SIZE } from './components/CloseButton';
import Footer from './components/Footer';
import { ROOT } from '../../../../../../constants/paths';
import take3 from '../../../../../../utils/take3';
import getPinnedApps from '../../../../../../utils/getPinnedApps';
import useFrameSizeSelector from '../../../../../../hooks/useFrameSizeSelector';
import useMessaging from '../../../../../../hooks/useMessaging';

const text = {
  header: {
    nl: 'Aanvullende diensten',
    en: 'Complementary services',
  },
};

const MultiApps: React.FC<Omit<ComponentProps, 'size'>> = ({
  dataTestId,
  onDismiss,
  onTrigger,
  ...rest
}) => {
  const widgetContext = useWidgetContext();
  const pathPrefix = usePathPrefix();
  const locale = useLocale();
  const currentBp = useFrameSizeSelector();
  const fireEvent = useFireTrackingEvent();
  const { appsOpened } = useStateContext();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const pinnedApps = getPinnedApps(Object.values(widgetContext.apps));

  const top3 =
    pinnedApps.length !== 0
      ? take3(pinnedApps)
      : take3(Object.values(widgetContext.apps));
  const amountOfOther = Object.keys(widgetContext.apps).length - top3.length;

  const sendMessage = useMessaging();

  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current !== null) {
        const clientBoundingRect = containerRef.current.getBoundingClientRect();
        sendMessage({
          type: 'resize',
          payload: {
            width: `${clientBoundingRect.width}px`,
            height: `${clientBoundingRect.height}px `,
            layout: 'collapsed',
            position: widgetContext.positions[currentBp],
          },
        });
      }
    }, 0);
  }, [currentBp, sendMessage, widgetContext, containerRef]);

  return (
    <Container
      ref={containerRef}
      {...rest}
      data-testid={dataTestId}
      $currentBp={currentBp}
      $size={Appearance.Large}
      $moreThanThree={top3.length >= 3}
    >
      <Header>
        <Heading2 withoutMargin>{text.header[locale]}</Heading2>
        <CloseButton
          onClick={e => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <Icon name="close" />
        </CloseButton>
      </Header>
      <TileList>
        {top3.map(app => {
          // When we disable apps from CROCS not having this check breaks the preview.
          if (isNil(app)) return null;

          const { title, slug, icon } = app;
          const path = `${pathPrefix}/apps/${slug}`;
          return (
            <Li key={app.slug}>
              <ListItem
                dataTestId={`multi-app-trigger-item-${app.slug}`}
                title={title[locale]}
                icon={icon}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const cleanedPath = cleanPath(path);
                  appsOpened.setValue(prev => {
                    if (!prev.includes(cleanedPath)) {
                      return [...prev, cleanedPath];
                    }
                    return prev;
                  });
                  onTrigger(cleanedPath);
                }}
              />
            </Li>
          );
        })}
      </TileList>

      {amountOfOther > 0 && (
        <Footer
          amountOfOther={amountOfOther}
          onClick={() => {
            onTrigger(ROOT);
            fireEvent({
              path: ROOT,
              event: 'ExpandWidget',
              triggerType: 'footerClick',
            });
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div<{
  $currentBp: 'desktop' | 'mobile';
  $size: Appearance;
  $moreThanThree?: boolean;
}>(
  ({ theme, $currentBp, $size }) => css`
    position: relative;
    border-radius: ${theme.getSystem().border.radius.base};
    width: ${iframeSizes.collapsed[$size][$currentBp].width};
    max-width: calc(
      ${iframeSizes.collapsed[$size][$currentBp].width} - ${CLOSE_BUTTON_SIZE}px
    );
    box-shadow: ${theme.boxShadow('s')};
    background-color: ${theme.color('primary')};

    display: flex;
    flex-direction: column;
    padding: ${theme.space('xxs')};

    h3 {
      color: ${theme.color('primary', 'text')};
    }
  `,
);

const Li = styled.li`
  &:last-child > div {
    margin-bottom: 0;
  }
`;

const Header = styled.header(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${arrayToCss(['xxs', 'xxxs', null, 'xxxs'], theme)};

    h2 {
      color: ${theme.color('primary', 'text')};
    }
  `,
);

const TileList = styled.ul(
  ({ theme }) => css`
    list-style: none;
    padding: 0;
    margin: ${arrayToCss(['xxs', null], theme)};
  `,
);

export default MultiApps;
